import { type FC, useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import EndTreatmentGroupModal from '@/components/EndTreatmentGroupModal';
import MoveGroupMembersModal from '@/components/MoveGroupMembersModal';
import TreatmentGroupActions from '@/components/TreatmentGroupActions';
import TreatmentGroupDetailSections from '@/components/TreatmentGroupDetailSections';
import type { Props } from '@/components/TreatmentGroupView/types';
import { TREATMENT_GROUP_VIEW_PARAM, TREATMENT_GROUPS_PATH } from '@/constants/treatmentGroups';
import AppContext from '@/contexts/appContext';
import TreatmentGroupForm from '@/forms/TreatmentGroupForm';
import { useDeleteTreatmentGroup, useTreatmentGroup } from '@/hooks/useTreatmentGroups';
import { TreatmentGroup } from '@/types/treatmentGroup';
import { Alert, Pill, Spinner, Typography } from '@/ui';
import { formatOptionalCalendarDate } from '@/utils/dates';
import { hasGroupEnded, uniqueCounselors } from '@/utils/treatmentGroupSessions';
import {
  hasPermission,
  TREATMENT_GROUP_CHANGE,
  TREATMENT_GROUP_DELETE
} from '@/utils/userPermission';

interface MovedMembersResult {
  copiedCount: number;
  destinationGroup: TreatmentGroup;
}

const TreatmentGroupView: FC<Props> = ({ onDeleted, onEditingChange, treatmentGroupId }) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { permissions } = useContext(AppContext);
  const canEdit = hasPermission({ permissions, requiredPermission: TREATMENT_GROUP_CHANGE });
  const canDelete = hasPermission({ permissions, requiredPermission: TREATMENT_GROUP_DELETE });

  const [isEditing, setIsEditing] = useState(false);
  const [pendingEndGroup, setPendingEndGroup] = useState<TreatmentGroup | null>(null);
  const [pendingMoveMembersGroup, setPendingMoveMembersGroup] = useState<TreatmentGroup | null>(
    null
  );
  const [movedMembersResult, setMovedMembersResult] = useState<MovedMembersResult | null>(null);

  const updateIsEditing = (nextIsEditing: boolean) => {
    setIsEditing(nextIsEditing);
    onEditingChange?.(nextIsEditing);
  };

  const { data: groupResponse, isPending, isError } = useTreatmentGroup(treatmentGroupId);
  const group = groupResponse?.data;

  const deleteTreatmentGroup = useDeleteTreatmentGroup();
  const handleDelete = (target: TreatmentGroup) => {
    if (!window.confirm(`Delete "${target.name}"? This cannot be undone.`)) return;
    deleteTreatmentGroup.mutate(target.id, {
      onSuccess: () => (onDeleted ? onDeleted() : navigate(TREATMENT_GROUPS_PATH))
    });
  };

  if (isPending) return <Spinner />;
  if (isError || !group) {
    return <Alert variant="error" message="Failed to load treatment group." />;
  }

  if (isEditing) {
    return (
      <div className="space-y-4">
        <Typography variant="heading">{group.name}</Typography>
        <TreatmentGroupForm
          treatmentGroup={group}
          onCancel={() => updateIsEditing(false)}
          onSuccess={() => updateIsEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Typography variant="heading">{group.name}</Typography>
          {group.is_draft && (
            <Pill size="sm" variant="subtle" color="warning">
              Draft
            </Pill>
          )}
        </div>
        <TreatmentGroupActions
          group={group}
          canEdit={canEdit}
          canDelete={canDelete}
          canEnd={canEdit}
          canMoveMembers={canEdit}
          onEdit={() => updateIsEditing(true)}
          onDelete={handleDelete}
          onEnd={setPendingEndGroup}
          onMoveMembers={setPendingMoveMembersGroup}
        />
      </div>

      {group.end_date &&
        (hasGroupEnded(group) ? (
          <Alert
            variant="error"
            message={`This group and all of its sessions ended on ${formatOptionalCalendarDate(group.end_date)}.`}
          />
        ) : (
          <Alert
            variant="info"
            message={`This group and all of its sessions will end on ${formatOptionalCalendarDate(group.end_date)}.`}
          />
        ))}

      {movedMembersResult && (
        <Alert variant="success" dismissable onDismiss={() => setMovedMembersResult(null)}>
          <p>
            <span className="font-semibold">
              {movedMembersResult.copiedCount} member
              {movedMembersResult.copiedCount === 1 ? '' : 's'}
            </span>{' '}
            added to {movedMembersResult.destinationGroup.name}. They&apos;re enrolled in all of its
            weekly sessions. This group hasn&apos;t changed.
          </p>
          <button
            type="button"
            className="mt-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
            onClick={() =>
              setSearchParams((params) => {
                const next = new URLSearchParams(params);
                next.set(TREATMENT_GROUP_VIEW_PARAM, movedMembersResult.destinationGroup.id);
                return next;
              })
            }>
            {`View ${uniqueCounselors(movedMembersResult.destinationGroup.sessions)[0]?.staff_name ?? movedMembersResult.destinationGroup.name}'s group`}
          </button>
        </Alert>
      )}

      <TreatmentGroupDetailSections group={group} timezone={group.timezone} />

      <EndTreatmentGroupModal group={pendingEndGroup} onClose={() => setPendingEndGroup(null)} />
      <MoveGroupMembersModal
        sourceGroup={pendingMoveMembersGroup}
        onClose={() => setPendingMoveMembersGroup(null)}
        onSuccess={(destinationGroup, copiedCount) => {
          setMovedMembersResult({ copiedCount, destinationGroup });
          setPendingMoveMembersGroup(null);
        }}
      />
    </div>
  );
};

export default TreatmentGroupView;
